import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Music, Send } from 'lucide-react';
import {
  getMusicInfo,
  getMyMusic,
  previewMusic,
  submitMusic,
  MusicItem,
  MusicPreview,
  MusicPublicInfo,
} from '../../../api/payment/music-request.api';
import MusicTierPicker from '../../../components/music-request/music-tier-picker';
import {
  defaultTierPrice,
  looksLikeYouTubeUrl,
  musicStatusLabel,
  queueEtaLabel,
  resolveTiers,
} from '../../../utils/music-request';
import usePollWhile from '../../../hooks/use-poll-while/use-poll-while';
import { LocalStorageService, RootState } from '../../../configuration';
import { routePaths } from '../../../routes/routes-path';

const MESSAGE_MAX_LEN = 150;
const PREVIEW_DEBOUNCE_MS = 500;

const formatCa = (v: number) => Number(v || 0).toLocaleString('vi-VN');

const modeSentence = (info: MusicPublicInfo): string =>
  info.mode === 'Approve'
    ? 'Streamer sẽ duyệt trước khi phát, bạn chỉ bị trừ Ca khi bài được duyệt.'
    : 'Bài vào hàng chờ và phát ngay khi tới lượt.';

/**
 * "Gửi nhạc" hiển thị cho NGƯỜI XEM trên trang creator, trong tab Donate — người xem dán
 * link YouTube, xem trước ảnh/tên bài rồi gửi kèm Ca. Chỉ hiện khi creator đã bật tính năng.
 */
const CreatorMusicRequest: React.FC<{
  userId?: string;
  onAvailableChange?: (available: boolean) => void;
}> = ({ userId, onAvailableChange }) => {
  const loggedIn = !!LocalStorageService.getItem('accessToken');
  const myProfile = useSelector((state: RootState) => state.features.auth.profile);
  const isOwner = !!myProfile?.userId && !!userId && myProfile.userId === userId;

  const [info, setInfo] = useState<MusicPublicInfo | null>(null);
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<MusicPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [tierPrice, setTierPrice] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState<MusicItem[]>([]);
  const [visible, setVisible] = useState(document.visibilityState === 'visible');
  // Chặn phản hồi preview trễ của link cũ ghi đè link đang gõ hiện tại (đổi link nhanh khi
  // request trước còn đang bay) — chỉ áp dụng phản hồi khớp với lần gọi mới nhất.
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (!userId) return;
    getMusicInfo(userId)
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [userId]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Xem trước link YouTube: debounce 500ms, chỉ gọi khi trông giống link YouTube — tránh gọi
  // API cho từng ký tự gõ dở và cho các link rõ ràng không phải YouTube.
  useEffect(() => {
    // Xoá preview cũ NGAY khi link đổi (không đợi debounce) — nút Gửi tắt ngay, không lỡ hiện
    // ảnh/tên của link cũ hoặc cho gửi trong lúc đang chờ preview của link mới.
    setPreview(null);
    setPreviewError(null);

    if (!looksLikeYouTubeUrl(url)) {
      setPreviewLoading(false);
      previewRequestIdRef.current += 1; // huỷ mọi phản hồi đang bay của lần gõ trước
      return undefined;
    }

    setPreviewLoading(true);
    previewRequestIdRef.current += 1;
    const requestId = previewRequestIdRef.current;
    const timer = setTimeout(() => {
      previewMusic(url)
        .then((res) => {
          if (previewRequestIdRef.current !== requestId) return; // đã có link mới hơn, bỏ qua
          setPreview(res);
          setPreviewError(null);
        })
        .catch((err) => {
          if (previewRequestIdRef.current !== requestId) return;
          setPreview(null);
          setPreviewError(err?.response?.data?.message || 'Không xem trước được video.');
        })
        .finally(() => {
          if (previewRequestIdRef.current === requestId) setPreviewLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [url]);

  const loadMine = useCallback(() => {
    if (!loggedIn || !userId) return;
    getMyMusic(userId)
      .then(setMine)
      .catch(() => {
        /* lỗi tải danh sách của tôi -> giữ nguyên danh sách cũ, không chặn form gửi bài */
      });
  }, [loggedIn, userId]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  usePollWhile(loggedIn && !!info?.enabled && visible, loadMine, 20000);

  // Chủ kênh xem trang cá nhân của chính mình -> không hiện khối gửi bài (không tự gửi cho mình).
  const available = !!info?.enabled && !isOwner;

  // Báo lên cha (thanh tab Donate) biết tính năng này có đang hiện hay không, để ẩn/hiện tab
  // tương ứng. Dùng ref giữ callback mới nhất để effect chỉ chạy lại khi `available` đổi,
  // không phải mỗi lần cha render lại truyền hàm mới.
  const onAvailableChangeRef = useRef(onAvailableChange);
  onAvailableChangeRef.current = onAvailableChange;
  useEffect(() => {
    onAvailableChangeRef.current?.(available);
    // Unmount (vd đổi tab lớn rồi quay lại) -> báo về false, tránh cha giữ mãi trạng thái
    // available cũ (component đã biến mất nhưng cha vẫn tưởng tab này còn hiện được).
    return () => onAvailableChangeRef.current?.(false);
  }, [available]);

  if (!available || !info) return null;

  const tiers = resolveTiers(info.tiers, info.price, info.maxSeconds);
  // Mức đã chọn không còn trong bảng (streamer vừa đổi giá) thì quay về mức thấp nhất.
  const chosenPrice =
    tierPrice !== null && tiers.some((t) => t.price === tierPrice) ? tierPrice : defaultTierPrice(tiers);
  const chosenTier = tiers.find((t) => t.price === chosenPrice) ?? null;

  const handleSubmit = () => {
    if (submitting || !preview || !userId) return;
    setSubmitting(true);
    submitMusic({
      creatorId: userId,
      url,
      message: message.trim() || null,
      isAnonymous,
      ...(chosenTier ? { tierPrice: chosenTier.price, tierSeconds: chosenTier.seconds } : {}),
    })
      .then(() => {
        toast.success('Đã gửi nhạc');
        setUrl('');
        setPreview(null);
        setMessage('');
        setIsAnonymous(false);
        loadMine();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Gửi nhạc thất bại');
        // Mức đã chọn không còn: tải lại bảng mức để người xem chọn lại.
        if (userId) getMusicInfo(userId).then(setInfo).catch(() => undefined);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Music className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Gửi nhạc</h3>
      </div>

      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{modeSentence(info)}</p>

      <div className="mt-3 flex flex-col gap-3">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Dán link YouTube của nhạc muốn gửi..."
          className="h-11 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white"
        />

        {previewLoading && (
          <p className="text-xs text-gray-400 dark:text-gray-500">Đang xem trước...</p>
        )}

        {previewError && (
          <p className="text-xs text-red-500 dark:text-red-400">{previewError}</p>
        )}

        {preview && (
          <div className="flex items-center gap-3 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-2">
            {preview.thumbnailUrl && (
              <img
                src={preview.thumbnailUrl}
                alt={preview.title}
                className="h-14 w-24 shrink-0 rounded object-cover"
              />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-800 dark:text-white">
                {preview.title}
              </p>
              {preview.author && (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {preview.author}
                </p>
              )}
            </div>
          </div>
        )}

        <MusicTierPicker tiers={tiers} selectedPrice={chosenPrice} onSelect={setTierPrice} />

        <textarea
          value={message}
          maxLength={MESSAGE_MAX_LEN}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Lời nhắn (không bắt buộc)"
          className="min-h-[60px] w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-base dark:text-white"
        />

        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          Gửi ẩn danh
        </label>

        {loggedIn ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !preview}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={16} />
            {submitting ? 'Đang gửi...' : `Gửi nhạc · ${formatCa(chosenPrice ?? info.price)} Ca`}
          </button>
        ) : (
          <Link
            to={routePaths.login}
            className="flex items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-base text-white"
          >
            Đăng nhập để gửi nhạc
          </Link>
        )}
      </div>

      {mine.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Bài của bạn
          </span>
          {mine.map((m) => {
            const eta = m.status === 'Queued' ? queueEtaLabel(m.position, m.etaSeconds) : '';
            return (
            <div
              key={m.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-2 text-sm"
            >
              <span className="truncate text-gray-700 dark:text-gray-300">{m.title}</span>
              <span className="shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                {eta || musicStatusLabel(m.status)}
              </span>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CreatorMusicRequest;
